import { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '@/components/SEO';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Shield,
  User,
  ArrowLeft,
  Linkedin,
  Building2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { getSpecialtyLabel } from '@/lib/specialtyLabels';

const professionDisplayNames: Record<string, string> = {
  'Occupational Therapist': 'Occupational Therapist',
  'Physical Therapist': 'Physical Therapist',
  'Social Worker': 'Social Worker',
  'Financial Advisor': 'Financial Advisor',
  'Elder Law Attorney': 'Elder Law Attorney',
  'Home Care Agency': 'Home Care Agency',
  'Geriatric Care Manager': 'Geriatric Care Manager',
  'Other': 'Senior Care Professional',
  'Independent Caregiver': 'Independent Caregiver',
  'OT': 'Occupational Therapist',
  'CAPS': 'Certified Aging-in-Place Specialist',
  'PROFESSIONAL': 'Senior Care Professional',
};

interface ProfessionalData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company_name: string;
  company_website: string;
  specialties: string[];
  service_areas: string[];
  experience_years: string;
  directory_slug: string;
  profession: string;
  license_state: string;
  availability: string;
  areas_of_expertise: string[];
  service_city: string;
  service_zip_code: string;
  website_url: string;
  professional_photo_url?: string;
  bio?: string;
  service_radius_miles?: number;
  role_title?: string;
  linkedin_url?: string;
  license_number?: string;
  license_verification_status?: string;
}

export default function ProfessionalProfile() {
  const { slug } = useParams<{ slug: string }>();
  const [professional, setProfessional] = useState<ProfessionalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const loadProfessional = async () => {
      if (!slug) return;
      
      try {
        const { data, error } = await supabase.functions.invoke('get-professional-directory', {
          body: { slug },
        });
        
        if (error) {
          setNotFound(true);
        } else if (data?.professional) {
          setProfessional(data.professional);
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error('Failed to load professional:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    loadProfessional();
  }, [slug]);

  if (loading) {
    // Derive a human-readable name from the slug for SEO during loading
    const slugName = slug
      ? slug.replace(/-[a-f0-9]{8}$/, '').split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      : 'Professional';

    return (
      <>
        <SEO
          title={`${slugName} - Senior Care Professional`}
          description={`View the professional profile for ${slugName} on the SeniorThrive Professional Directory.`}
          canonical="https://seniorthrive.com/professionals/directory"
          noindex
        />
        <div className="container mx-auto px-4 py-10">
          <div className="text-center text-muted-foreground">Loading professional profile...</div>
        </div>
      </>
    );
  }

  if (notFound || !professional) {
    // Derive name from slug for SEO, do NOT redirect, because pre-renderers
    // follow the redirect and cache the directory page's meta tags instead.
    const slugName = slug
      ? slug.replace(/-[a-f0-9]{8}$/, '').split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      : 'Professional';

    return (
      <main className="container mx-auto px-4 py-10 max-w-5xl">
        <SEO
          title={`${slugName} - Senior Care Professional`}
          description={`View the professional profile for ${slugName} on the SeniorThrive Professional Directory.`}
          canonical="https://seniorthrive.com/professionals/directory"
          noindex
        />
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-foreground mb-4">{slugName}</h1>
          <p className="text-muted-foreground mb-6">
            This profile could not be loaded. It may have been removed or is temporarily unavailable.
          </p>
          <Link
            to="/professionals/directory"
            className="inline-flex items-center gap-2 text-primary hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Browse the Professional Directory
          </Link>
        </div>
      </main>
    );
  }

  const fullName = `${professional.first_name} ${professional.last_name}`;
  const professionLabel = professionDisplayNames[professional.profession] || professional.profession;
  const locationParts = [professional.service_city, professional.license_state].filter(Boolean);
  const locationText = locationParts.join(', ');

  const getLocationDisplay = () => {
    const parts = [
      professional.service_city,
      professional.license_state,
      professional.service_zip_code
    ].filter(Boolean);
    
    if (parts.length === 0) return null;
    return parts.join(', ');
  };

  const getServiceRadiusDisplay = () => {
    if (professional.service_radius_miles) {
      return `${professional.service_radius_miles} mile service radius`;
    }
    return null;
  };

  const formatUrl = (url: string) => {
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  const getSpecialties = () => {
    const specialties = professional.specialties || professional.areas_of_expertise || [];
    return specialties.filter(Boolean);
  };

  const isVerified = professional.license_verification_status === 'verified';

  const websiteUrl = professional.website_url || professional.company_website;

  return (
    <main className="container mx-auto px-4 py-10 max-w-5xl">
      {/*
        SEO containment (April 2026 audit):
        - noindex individual profiles, directory is thin content by design
        - canonical points to the /professionals/directory hub so any indexed variants consolidate
        - JSON-LD Person schema intentionally removed to avoid thin-content rich-result signals
        - Profile is still fully accessible to humans and linked from the directory
      */}
      <SEO
        title={`${fullName} - ${professionLabel}${locationText ? ` in ${locationText}` : ''}`}
        description={`${fullName} is a ${professionLabel}${locationText ? ` serving ${locationText}` : ''}. ${professional.bio ? professional.bio.slice(0, 120) : 'View contact details and specialties on SeniorThrive.'}`}
        canonical="https://seniorthrive.com/professionals/directory"
        image={professional.professional_photo_url}
        url={`https://seniorthrive.com/professionals/${slug}`}
        type="profile"
        noindex
      />
      <Helmet>
        {/* Override SEO component's noindex,nofollow, we want Google to follow outlinks
            from profile → directory hub to strengthen hub authority. */}
        <meta name="robots" content="noindex,follow" />
        <meta property="og:title" content={`${fullName} - ${professionLabel}`} />
        <meta property="og:description" content={`${fullName} is a ${professionLabel}${locationText ? ` serving ${locationText}` : ''}.`} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={`https://seniorthrive.com/professionals/${slug}`} />
        {professional.professional_photo_url && <meta property="og:image" content={professional.professional_photo_url} />}
      </Helmet>

      {/* Back Navigation */}
      <div className="mb-6">
        <Link 
          to="/professionals/directory" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Directory
        </Link>
      </div>

      <div className="grid gap-8 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile Header Card */}
          <Card className="rounded-md border bg-white shadow-raised overflow-hidden">
            <CardHeader className="pb-0">
              <div className="flex items-start gap-5">
                {professional.professional_photo_url ? (
                  <img
                    src={professional.professional_photo_url}
                    alt={`${fullName} profile`}
                    className="w-20 h-20 rounded-full object-cover border-2 border-border shrink-0"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center border-2 border-border shrink-0">
                    <User className="w-10 h-10 text-muted-foreground" />
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-3 flex-wrap mb-1">
                    <h1 className="text-2xl font-bold text-foreground">
                      {fullName}
                    </h1>
                    {isVerified && (
                      <Badge className="bg-st_light_blue text-white shrink-0">
                        <Shield className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                  </div>

                  {/* Role Title - italic subtitle */}
                  {professional.role_title && (
                    <p className="text-muted-foreground italic mb-1">
                      {professional.role_title}
                    </p>
                  )}
                  
                  <p className="text-primary font-medium mb-3">
                    {professionDisplayNames[professional.profession] || professional.profession}
                  </p>

                  {/* Location & Service Radius */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    {getLocationDisplay() && (
                      <div className="flex items-center gap-1.5">
                        <MapPin className="w-4 h-4" />
                        <span>{getLocationDisplay()}</span>
                      </div>
                    )}
                    {getServiceRadiusDisplay() && (
                      <span className="text-muted-foreground/70">
                        • {getServiceRadiusDisplay()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-6 space-y-6">
              {/* Bio */}
              {professional.bio && (
                <div>
                  <h2 className="text-sm font-bold text-st_taupe uppercase tracking-wide mb-2">About</h2>
                  <p className="text-foreground leading-relaxed">
                    {professional.bio}
                  </p>
                </div>
              )}

              {/* Specialties */}
              {getSpecialties().length > 0 && (
                <div>
                  <h2 className="text-sm font-bold text-st_taupe uppercase tracking-wide mb-3">Areas of Focus</h2>
                  <div className="flex flex-wrap gap-2">
                    {getSpecialties().map((specialty) => (
                      <Badge key={specialty} variant="secondary" className="text-sm">
                        {getSpecialtyLabel(specialty)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Company */}
              {professional.company_name && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Building2 className="w-4 h-4" />
                  <span>{professional.company_name}</span>
                </div>
              )}

              {/* Experience */}
              {professional.experience_years && (
                <div>
                  <h2 className="text-sm font-bold text-st_taupe uppercase tracking-wide mb-2">Experience</h2>
                  <p className="text-foreground">{professional.experience_years} years of professional experience</p>
                </div>
              )}

              {/* Service Area */}
              {(professional.service_areas?.length > 0 || getLocationDisplay()) && (
                <div>
                  <h2 className="text-sm font-bold text-st_taupe uppercase tracking-wide mb-2">Service Area</h2>
                  {getLocationDisplay() && (
                    <p className="text-foreground mb-1">Based in {getLocationDisplay()}</p>
                  )}
                  {getServiceRadiusDisplay() && (
                    <p className="text-muted-foreground text-sm">{getServiceRadiusDisplay()}</p>
                  )}
                  {professional.service_areas?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {professional.service_areas.map((area) => (
                        <Badge key={area} variant="outline" className="text-sm">
                          {area}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Availability */}
              {professional.availability && (
                <div>
                  <h2 className="text-sm font-bold text-st_taupe uppercase tracking-wide mb-2">Availability</h2>
                  <p className="text-foreground">{professional.availability}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Contact Sidebar */}
        <div className="lg:col-span-1">
          <Card className="rounded-md border bg-white shadow-raised sticky top-6">
            <CardHeader className="pb-4">
              <h2 className="text-lg font-bold">Get in Touch</h2>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Primary CTA */}
              {professional.email && (
                <Button 
                  size="lg" 
                  className="w-full bg-st_light_blue hover:bg-st_light_blue/90"
                  asChild
                >
                  <a href={`mailto:${professional.email}`}>
                    <Mail className="w-4 h-4 mr-2" />
                    Request Consultation
                  </a>
                </Button>
              )}
              
              {/* Phone */}
              {professional.phone && (
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="w-full"
                  onClick={() => window.open(`tel:${professional.phone}`, '_blank')}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  {professional.phone}
                </Button>
              )}
              
              {/* Website */}
              {websiteUrl && (
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="w-full"
                  onClick={() => window.open(formatUrl(websiteUrl), '_blank')}
                >
                  <Globe className="w-4 h-4 mr-2" />
                  Visit Website
                </Button>
              )}

              {/* LinkedIn */}
              {professional.linkedin_url && (
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="w-full"
                  onClick={() => window.open(formatUrl(professional.linkedin_url!), '_blank')}
                >
                  <Linkedin className="w-4 h-4 mr-2" />
                  LinkedIn Profile
                </Button>
              )}

              {/* License Info */}
              {professional.license_state && (
                <div className="pt-4 mt-4 border-t border-border/50">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Licensed In</p>
                  <p className="text-sm font-medium">{professional.license_state}</p>
                  {professional.license_number && (
                    <p className="text-xs text-muted-foreground mt-1">
                      License #{professional.license_number}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </main>
  );
}
